import DropdownSelector from 'components/DropdownSelector';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { RRule } from 'rrule';
import { getFixedDtstart, getOrdinalMap, getWeekdayMap } from 'utils/recurrenceUtils';
import TimeSlot from 'features/Responder/features/Schedules/features/ScheduleBuilder/components/TimeSlot';
import DateSelector from './components/DateSelector';
import EnableDaysOfWeekSwitch from './components/EnableDaysOfWeekSwitch';
import EndDateSelector from './components/EndDateSelector';
import EndRepeatSelector from './components/EndRepeatSelector';
import { ExtraTimeSlots } from './components/ExtraTimeSlots';
import FrequencySelector from './components/FrequencySelector';
import IntervalSelector from './components/IntervalSelector';
import MonthDaysSelector from './components/MonthDaysSelector';
import MonthlyRepeatTypeSelector from './components/MonthlyRepeatTypeSelector';
import MonthsSelector from './components/MonthsSelector';
import OnTheSelector from './components/OnTheSelector';
import RepeatTypeSelector from './components/RepeatTypeSelector';
import WeekdaysSelector from './components/WeekdaysSelector';

export const RRuleBuilder = ({
  R,
  setR,
  activeEditSchedule,
  startTime,
  //timeslot vars
  endTime,
  scheduleDuration,
  useTourDuration,
  notAllDayTimeStart,
  setNotAllDayTimeStart,
  setUseTourDuration,
  setScheduleDuration,
  setStartTime,
  setEndTime,
  setOldTimeSlot,
  setOldScheduleDuration,
  oldTimeSlot,
  oldScheduleDuration,
  isZoneSelected,
  selectedTour,
  tours,
  getTotalDurationOfTour,
  shouldShowErrorIndicators,
  hasTimeError,
  isTargetSelected,
  startTimeField,
  endTimeField,
  extraStartTimes,
  setExtraStartTimes,
}) => {
  if (!R || !setR) {
    throw new Error('RRule and setRRule required');
  }

  // Always use user's current time zone as default
  const userTzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzid = userTzid;
  const getTodayDateString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [startDate, setStartDate] = useState(getTodayDateString());

  // Reset all state and R on component unmount
  useEffect(() => {
    return () => {
      setStartDate(null);
      setRepeatType('never');
      setEndRepeat('never');
      setEndDate(null);
      setFrequency('day');
      setIntervalFrequency(1);
      setSelectedWeekdays([]);
      setSelectedMonths([]);
      setSelectedMonthDays([]);
      setEnableDaysOfWeek(false);
      setOnTheOption({ first: 'first', day: 'sunday' });
      setR(
        new RRule({
          freq: RRule.DAILY,
          interval: 1,
          count: 1,
          wkst: RRule.SU,
          tzid: userTzid,
        }),
      );
    };
  }, []);

  // Initialize selectedMonthDays with current dtstart day if available
  const getInitialMonthDay = () => {
    // Try to get from initial RRule dtstart, else from startDate
    let dt = null;
    if (R && R.options && R.options.dtstart) {
      dt = new Date(getFixedDtstart(R.options.dtstart));
    } else if (startDate) {
      dt = new Date(startDate);
    }
    if (dt && !isNaN(dt.getTime())) {
      return [dt.getDate()];
    }
    return [];
  };

  const getDefaultEndDate = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const yyyy = tomorrow.getFullYear();
    const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const dd = String(tomorrow.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const initOptions = useMemo(() => {
    // console.log('RRuleBuilder getInitOptions called with R:', R, 'startDate:', startDate);
    let options = {
      repeatType: 'never',
      startDate: null,
      endRepeat: 'never',
      endDate: null,
      frequency: 'day',
      intervalFrequency: 1,
      selectedWeekdays: [],
      selectedMonths: [],
      selectedMonthDays: getInitialMonthDay(),
      onTheOption: { first: 'first', day: 'sunday' },
      enableDaysOfWeek: false,
      useThisTzid: userTzid,
    };
    //overwrite if R has values
    if (R && R.options) {
      // Extract startDate from dtstart if available
      if (R.options.dtstart) {
        // console.log('R.string:', R.toString());
        // console.log('R.options.dtstart found:', R.options.dtstart);
        const dt = new Date(R.options.dtstart);
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        options.startDate = `${yyyy}-${mm}-${dd}`;
        // console.log('Parsed startDate from R.options.dtstart:', options.startDate);
      }
      //repeatType
      if (R.options.count === 1) {
        options.repeatType = 'never';
      } else if (R.options.freq === RRule.DAILY && R.options.interval === 1) {
        options.repeatType = 'daily';
      } else if (
        R.options.freq === RRule.WEEKLY &&
        R.options.interval === 1 &&
        !R.options.byweekday
      ) {
        options.repeatType = 'weekly';
      } else if (
        R.options.freq === RRule.WEEKLY &&
        R.options.interval === 2 &&
        !R.options.byweekday
      ) {
        options.repeatType = 'biweekly';
      } else if (R.options.freq === RRule.WEEKLY && R.options.byweekday) {
        // byweekday is an array of numbers (0=MO, 1=TU, ..., 6=SU) or RRule weekday objects
        const weekdayList = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
        const convertedWeekdays = R.options.byweekday
          .map((wd) => {
            // If wd is a number, map directly; if RRule weekday object, use .weekday
            if (typeof wd === 'number') {
              return weekdayList[wd % 7];
            } else if (typeof wd === 'object' && typeof wd.weekday === 'number') {
              return weekdayList[wd.weekday % 7];
            } else if (typeof wd === 'string') {
              // fallback for string codes
              return wd;
            }
            return null;
          })
          .filter(Boolean);
        options.repeatType = 'custom';
        options.selectedWeekdays = convertedWeekdays;
      } else if (
        R.options.freq === RRule.MONTHLY &&
        R.options.interval === 1 &&
        !R.options.bymonthday
      ) {
        options.repeatType = 'monthly';
      } else if (
        R.options.freq === RRule.YEARLY &&
        R.options.interval === 1 &&
        !R.options.bymonth
      ) {
        options.repeatType = 'yearly';
      } else {
        options.repeatType = 'custom';
      }

      //endRepeat
      if (R.options.until) {
        options.endRepeat = 'date';
        const until = new Date(R.options.until);
        const yyyy = until.getFullYear();
        const mm = String(until.getMonth() + 1).padStart(2, '0');
        const dd = String(until.getDate()).padStart(2, '0');
        options.endDate = `${yyyy}-${mm}-${dd}`;
      }
      //frequency
      switch (R.options.freq) {
        case RRule.DAILY:
          options.frequency = 'day';
          break;
        case RRule.WEEKLY:
          options.frequency = 'week';
          break;
        case RRule.MONTHLY:
          options.frequency = 'month';
          break;
        case RRule.YEARLY:
          options.frequency = 'year';
          break;
        default:
          options.frequency = 'day';
      }
      //intervalFrequency
      options.intervalFrequency = R.options.interval || 1;
      //selectedWeekdays
      if (R.options.byweekday) {
        // Convert byweekday numeric indices to weekday string codes (MO, TU, etc)
        const weekdayList = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
        options.selectedWeekdays = R.options.byweekday
          .map((wd) => {
            if (typeof wd === 'number') {
              return weekdayList[wd % 7];
            } else if (typeof wd === 'object' && typeof wd.weekday === 'number') {
              return weekdayList[wd.weekday % 7];
            } else if (typeof wd === 'string') {
              return wd;
            }
            return null;
          })
          .filter(Boolean);
      }
      //selectedMonths
      if (R.options.bymonth) {
        options.selectedMonths = Array.isArray(R.options.bymonth)
          ? R.options.bymonth
          : [R.options.bymonth];
      }
      //selectedMonthDays
      if (R.options.bymonthday) {
        options.selectedMonthDays = Array.isArray(R.options.bymonthday)
          ? R.options.bymonthday
          : [R.options.bymonthday];
      } else {
        // If no bymonthday, default to startDate day
        options.selectedMonthDays = getInitialMonthDay();
      }
      if (R.options.bynweekday) {
        // console.log('R.options.bynweekday:', R.options.bynweekday, R.toString());
        // onTheOption
        const ordinalMap = getOrdinalMap();
        // Map RRule weekday numbers to lowercase day names for OnTheSelector
        const weekdayToNameMap = {
          0: 'monday',
          1: 'tuesday',
          2: 'wednesday',
          3: 'thursday',
          4: 'friday',
          5: 'saturday',
          6: 'sunday',
        };
        const bynweekdayArr = Array.isArray(R.options.bynweekday)
          ? R.options.bynweekday
          : [R.options.bynweekday];
        // Support [weekday, ordinal] tuple format
        const mappedOnTheOptions = bynweekdayArr
          .filter((item) => {
            // Accept either {weekday, n} object or [weekday, ordinal] tuple
            return (
              (Array.isArray(item) &&
                item.length === 2 &&
                typeof item[0] === 'number' &&
                typeof item[1] === 'number') ||
              (item && typeof item === 'object' && typeof item.weekday === 'number')
            );
          })
          .map((item) => {
            let weekday, n;
            if (Array.isArray(item)) {
              weekday = item[0];
              n = item[1];
            } else {
              weekday = item.weekday;
              n = item.n;
            }
            const day = weekdayToNameMap[weekday % 7] || 'sunday';
            let first = 'first';
            if (n === -1) first = 'last';
            else if (n === -2) first = 'nexttolast';
            else if (n > 0) first = ordinalMap[n] || 'first';
            return { first, day };
          });
        if (mappedOnTheOptions.length === 1) {
          options.onTheOption = mappedOnTheOptions[0];
        } else if (mappedOnTheOptions.length > 1) {
          options.onTheOption = mappedOnTheOptions;
        }
      }
      if (R.options.tzid && activeEditSchedule) {
        let opts = RRule.parseString(activeEditSchedule.recurrence);
        options.useThisTzid = opts.tzid;
        // console.log('Using tzid from R.options:', options.useThisTzid);
      }
      //enableDaysOfWeek
      options.enableDaysOfWeek = R.options.byweekday || R.options.bynweekday ? true : false;
      // Always default to user's tzid if not present
      if (!options.useThisTzid) {
        options.useThisTzid = userTzid;
      }
    }
    if (options.selectedMonthDays.length === 0) {
      options.selectedMonthDays = [1];
    }

    return options;
  }, [activeEditSchedule]);

  const [repeatType, setRepeatType] = useState(initOptions.repeatType);
  const [endRepeat, setEndRepeat] = useState(initOptions.endRepeat);
  const [endDate, setEndDate] = useState(initOptions.endDate);
  const [frequency, setFrequency] = useState(initOptions.frequency);
  const [intervalFrequency, setIntervalFrequency] = useState(initOptions.intervalFrequency);
  const [selectedWeekdays, setSelectedWeekdays] = useState(initOptions.selectedWeekdays);
  const [selectedMonths, setSelectedMonths] = useState(initOptions.selectedMonths);
  const [selectedMonthDays, setSelectedMonthDays] = useState(initOptions.selectedMonthDays);
  const [enableDaysOfWeek, setEnableDaysOfWeek] = useState(initOptions.enableDaysOfWeek);
  const [onTheOption, setOnTheOption] = useState(initOptions.onTheOption);
  const [useThisTzid, setUseThisTzid] = useState(initOptions.useThisTzid);

  // Re-initialize local state when activeEditSchedule changes
  useEffect(() => {
    // When a new schedule is selected, set startDate to the schedule's dtstart (midnight GMT, correct calendar day)
    let newStartDate = initOptions.startDate;
    if (activeEditSchedule && activeEditSchedule.recurrence) {
      try {
        const opts = RRule.parseString(activeEditSchedule.recurrence);
        if (opts.dtstart) {
          const dt = new Date(opts.dtstart);
          // Use UTC parts to preserve the intended calendar day in GMT
          const yyyy = dt.getUTCFullYear();
          const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
          const dd = String(dt.getUTCDate()).padStart(2, '0');
          newStartDate = `${yyyy}-${mm}-${dd}`;
        }
      } catch (e) {
        // fallback to initOptions.startDate
      }
    }
    setStartDate(newStartDate);
    setRepeatType(initOptions.repeatType);
    setEndRepeat(initOptions.endRepeat);
    setEndDate(initOptions.endDate);
    setFrequency(initOptions.frequency);
    setIntervalFrequency(initOptions.intervalFrequency);
    setSelectedWeekdays(initOptions.selectedWeekdays);
    setSelectedMonths(initOptions.selectedMonths);
    // Only update selectedMonthDays if it is different from the current state
    if (
      !Array.isArray(selectedMonthDays) ||
      selectedMonthDays.length !== initOptions.selectedMonthDays.length ||
      selectedMonthDays.some((v, i) => v !== initOptions.selectedMonthDays[i])
    ) {
      setSelectedMonthDays(initOptions.selectedMonthDays);
    }
    setOnTheOption(initOptions.onTheOption);
    setEnableDaysOfWeek(initOptions.enableDaysOfWeek);
    setUseThisTzid(initOptions.useThisTzid);
  }, [activeEditSchedule, initOptions]);

  // useEffect(() => {
  //   console.log('usethisTzid changed to:', useThisTzid);
  //   console.log('Current R.options.tzid:', R.options.tzid);
  // }, [useThisTzid]);
  // Clear extra hours if all day is selected or repeatType is 'never'
  useEffect(() => {
    if (scheduleDuration === 1439 || repeatType === 'never') {
      setStartTime((prev) => {
        if (Array.isArray(prev) && prev.length > 1) {
          return [prev[0]];
        }
        return prev;
      });
      setEndTime((prev) => {
        if (Array.isArray(prev) && prev.length > 1) {
          return [prev[0]];
        }
        return prev;
      });
    }
  }, [scheduleDuration, repeatType, setStartTime]);

  // // Repopulate selectedMonthDays to dtstart day if it becomes empty
  useEffect(() => {
    if (selectedMonthDays.length === 0) {
      let dt = null;
      if (R && R.options && R.options.dtstart) {
        dt = new Date(R.options.dtstart);
      } else if (startDate) {
        dt = new Date(startDate);
      }
      if (dt && !isNaN(dt.getTime())) {
        setSelectedMonthDays([dt.getDate()]);
      }
    }
  }, [selectedMonthDays, startDate]);

  const bypassOptions = {
    // byhour: undefined,
    // byminute: undefined,
    bysecond: 0,
    wkst: RRule.SU,
    bynmonthday: undefined,
    bynweekday: undefined,
  };

  // Centralize all repeatType logic here
  useEffect(() => {
    // Always set dtstart from startDate as a local date at midnight
    let dtstart = undefined;
    if (startDate) {
      const [year, month, day] = startDate.split('-').map(Number);
      dtstart = new Date(year, month - 1, day);

      // Set dtstart to 00:00:00 GMT (UTC), preserving the intended calendar day
      dtstart = new Date(
        Date.UTC(dtstart.getFullYear(), dtstart.getMonth(), dtstart.getDate(), 0, 0, 0, 0),
      );
    }

    let options = { ...R.options, ...bypassOptions, dtstart: dtstart };

    // Set byhour and byminute to [0] if all day
    if (scheduleDuration === 1439) {
      options.byhour = [0];
      options.byminute = [0];
    } else if (Array.isArray(startTime)) {
      const byhourArr = startTime
        .map((t) =>
          typeof t === 'string' && t.includes(':') ? Number(t.split(':')[0]) : undefined,
        )
        .filter((v) => v !== undefined);
      const byminuteNum =
        startTime[0] && typeof startTime[0] === 'string' && startTime[0].includes(':')
          ? Number(startTime[0].split(':')[1])
          : undefined;
      options.byhour = byhourArr.length > 0 ? byhourArr : undefined;
      options.byminute = byminuteNum !== undefined ? [byminuteNum] : undefined;
    } else if (startTime && typeof startTime === 'string' && startTime.includes(':')) {
      const [hour, minute] = startTime.split(':').map(Number);
      options.byhour = hour;
      options.byminute = [minute];
    } else {
      options.byhour = undefined;
      options.byminute = undefined;
    }

    // Default interval for non-custom types
    let presetInterval = 1;
    switch (repeatType) {
      case 'never':
        options.freq = RRule.DAILY;
        presetInterval = 1;
        options.count = 1;
        options.bymonth = null;
        options.bymonthday = null;
        options.byweekday = null;
        options.until = null;
        options.byhour = options.byhour[0];
        options.byminute = options.byminute[0];

        setEndRepeat('never');
        break;
      case 'daily':
        options.freq = RRule.DAILY;
        presetInterval = 1;
        options.count = null;
        options.bymonth = null;
        options.bymonthday = null;
        options.byweekday = null;
        break;
      case 'weekly': {
        options.freq = RRule.WEEKLY;
        presetInterval = 1;
        options.count = null;
        options.bymonth = null;
        options.bymonthday = null;
        if (dtstart) {
          const weekday = new Date(dtstart).getDay();
          const rruleWeekdays = [
            RRule.SU,
            RRule.MO,
            RRule.TU,
            RRule.WE,
            RRule.TH,
            RRule.FR,
            RRule.SA,
          ];
          options.byweekday = [rruleWeekdays[weekday]];
        } else {
          options.byweekday = null;
        }
        break;
      }
      case 'weekday':
        options.freq = RRule.WEEKLY;
        presetInterval = 1;
        options.count = null;
        options.byweekday = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR];
        options.bymonth = null;
        options.bymonthday = null;
        break;
      case 'biweekly': {
        options.freq = RRule.WEEKLY;
        presetInterval = 2;
        options.count = null;
        options.bymonth = null;
        options.bymonthday = null;
        if (dtstart) {
          const weekday = new Date(dtstart).getDay();
          const rruleWeekdays = [
            RRule.SU,
            RRule.MO,
            RRule.TU,
            RRule.WE,
            RRule.TH,
            RRule.FR,
            RRule.SA,
          ];
          options.byweekday = [rruleWeekdays[weekday]];
        } else {
          options.byweekday = null;
        }
        break;
      }
      case 'monthly': {
        options.freq = RRule.MONTHLY;
        presetInterval = 1;
        options.count = null;
        options.bymonth = null;
        options.byweekday = null;
        // Set bymonthday from dtstart if available
        if (dtstart) {
          options.bymonthday = new Date(dtstart).getDate();
        } else {
          options.bymonthday = null;
        }
        break;
      }
      case 'yearly': {
        options.freq = RRule.YEARLY;
        presetInterval = 1;
        options.count = null;
        options.byweekday = null;
        // Set bymonth and bymonthday from dtstart if available
        if (dtstart) {
          const date = new Date(dtstart);
          options.bymonth = date.getMonth() + 1;
          options.bymonthday = date.getDate();
        } else {
          options.bymonth = null;
          options.bymonthday = null;
        }
        break;
      }
      case 'custom':
        // Always clear bymonth and bymonthday when switching to custom
        options.bymonth = null;
        options.bymonthday = null;
        // Use the current frequency prop to set freq
        switch (frequency) {
          case 'day':
            options.freq = RRule.DAILY;
            options.byweekday = null;
            break;
          case 'week':
            options.freq = RRule.WEEKLY;
            options.byweekday =
              selectedWeekdays.length > 0 ? selectedWeekdays.map((d) => RRule[d]) : null;
            break;
          case 'month':
            options.freq = RRule.MONTHLY;
            options.byweekday = null;
            break;
          case 'year':
            options.freq = RRule.YEARLY;
            options.bymonth = selectedMonths.length > 0 ? selectedMonths : null;
            if (!enableDaysOfWeek && dtstart) {
              options.bymonthday = dtstart ? dtstart.getDate() : null;
            }
            options.byweekday = null;
            break;
          default:
            options.freq = RRule.DAILY;
            options.byweekday = null;
        }
        options.count = null;
        break;
      default:
        options.freq = RRule.DAILY;
        presetInterval = 1;
        options.count = null;
        options.bymonth = null;
        options.bymonthday = null;
        options.byweekday = null;
    }

    // Set interval: custom uses intervalFrequency, others use presetInterval
    options.interval = repeatType === 'custom' ? intervalFrequency || 1 : presetInterval;

    // Handle endRepeat and endDate logic
    if (endRepeat === 'never') {
      options.until = null;
    } else if (endRepeat === 'date' && endDate) {
      const [year, month, day] = endDate.split('-').map(Number);
      options.until = new Date(year, month - 1, day, 23, 59, 59);
    }

    if (enableDaysOfWeek && (frequency === 'month' || frequency === 'year')) {
      let byweekday = undefined;
      if (enableDaysOfWeek) {
        const dayMap = {
          sunday: RRule.SU,
          monday: RRule.MO,
          tuesday: RRule.TU,
          wednesday: RRule.WE,
          thursday: RRule.TH,
          friday: RRule.FR,
          saturday: RRule.SA,
          day: RRule.DAY,
          weekday: RRule.WEEKDAY,
          weekendday: RRule.WEEKENDDAY,
        };
        if (onTheOption.day in dayMap && dayMap[onTheOption.day] != null) {
          const ordinalMap = {
            first: 1,
            second: 2,
            third: 3,
            fourth: 4,
            fifth: 5,
            nexttolast: -2,
            last: -1,
          };
          const ordinal = ordinalMap[onTheOption.first];
          const dayValue = dayMap[onTheOption.day];
          if (dayValue && typeof dayValue.nth === 'function') {
            byweekday = [dayValue.nth(ordinal)];
          }
        }
        options.byweekday = byweekday;
        options.bymonthday = undefined;
      } else {
        options.byweekday = undefined;
        options.bynweekday = undefined;
        if (frequency === 'month') {
          options.bymonthday =
            selectedMonthDays.length > 0 ? selectedMonthDays : dtstart ? [dtstart.getDate()] : null;
        }
      }
    }

    if (!enableDaysOfWeek && frequency === 'month') {
      options.byweekday = null;
      options.bynweekday = null;
      options.bymonthday =
        selectedMonthDays.length > 0 ? selectedMonthDays : dtstart ? [dtstart.getDate()] : null;
    }
    options.tzid = useThisTzid;
    options.bysecond = 0;

    setR(new RRule(options));
  }, [
    repeatType,
    frequency,
    startDate,
    startTime,
    selectedWeekdays,
    endRepeat,
    endDate,
    enableDaysOfWeek,
    onTheOption,
    intervalFrequency,
    selectedMonthDays,
    tzid,
    selectedMonths,
    useThisTzid,
  ]);

  return (
    <Form.Group className="mb-3 mt-3">
      <RepeatTypeSelector repeatType={repeatType} setRepeatType={setRepeatType} />
      {repeatType === 'custom' && (
        <FrequencySelector frequency={frequency} setFrequency={setFrequency} />
      )}
      {(frequency === 'day' ||
        frequency === 'week' ||
        frequency === 'month' ||
        frequency === 'year') &&
        repeatType === 'custom' && (
          <IntervalSelector
            intervalFrequency={intervalFrequency}
            setIntervalFrequency={setIntervalFrequency}
            frequency={frequency}
          />
        )}
      {frequency === 'week' && repeatType === 'custom' && (
        <WeekdaysSelector
          selectedWeekdays={selectedWeekdays}
          setSelectedWeekdays={setSelectedWeekdays}
        />
      )}
      {frequency === 'year' && repeatType === 'custom' && (
        <MonthsSelector selectedMonths={selectedMonths} setSelectedMonths={setSelectedMonths} />
      )}
      {frequency === 'year' && repeatType === 'custom' && (
        <EnableDaysOfWeekSwitch
          enabled={!!enableDaysOfWeek && enableDaysOfWeek !== 'never'}
          onChange={(e) => setEnableDaysOfWeek(e.target.checked)}
        />
      )}
      {frequency === 'year' && repeatType === 'custom' && enableDaysOfWeek && (
        <OnTheSelector option={onTheOption} setOption={setOnTheOption} />
      )}
      {frequency === 'month' && repeatType === 'custom' && (
        <MonthlyRepeatTypeSelector
          enableDaysOfWeek={enableDaysOfWeek}
          setEnableDaysOfWeek={setEnableDaysOfWeek}
        />
      )}
      {frequency === 'month' && repeatType === 'custom' && !enableDaysOfWeek && (
        <MonthDaysSelector
          selectedMonthDays={selectedMonthDays}
          setSelectedMonthDays={setSelectedMonthDays}
        />
      )}
      {frequency === 'month' && repeatType === 'custom' && enableDaysOfWeek && (
        <OnTheSelector option={onTheOption} setOption={setOnTheOption} />
      )}
      <TimeSlot
        startTime={startTime[0]}
        endTime={endTime[0]}
        scheduleDuration={scheduleDuration}
        useTourDuration={useTourDuration}
        notAllDayTimeStart={notAllDayTimeStart}
        setNotAllDayTimeStart={setNotAllDayTimeStart}
        setUseTourDuration={setUseTourDuration}
        setScheduleDuration={setScheduleDuration}
        setStartTime={(val) =>
          setStartTime((prev) => {
            if (Array.isArray(prev)) {
              const arr = [...prev];
              arr[0] = val;
              return arr;
            }
            return [val];
          })
        }
        setEndTime={(val) =>
          setEndTime((prev) => {
            if (Array.isArray(prev)) {
              const arr = [...prev];
              arr[0] = val;
              return arr;
            }
            return [val];
          })
        }
        setOldTimeSlot={setOldTimeSlot}
        setOldScheduleDuration={setOldScheduleDuration}
        oldTimeSlot={oldTimeSlot}
        oldScheduleDuration={oldScheduleDuration}
        isZoneSelected={isZoneSelected}
        selectedTour={selectedTour}
        tours={tours}
        getTotalDurationOfTour={getTotalDurationOfTour}
        shouldShowErrorIndicators={shouldShowErrorIndicators}
        hasTimeError={hasTimeError}
        isTargetSelected={isTargetSelected}
        startTimeField={startTimeField}
        endTimeField={endTimeField}
      />
      {repeatType !== 'never' && scheduleDuration !== 1439 && (
        <ExtraTimeSlots
          scheduleDuration={scheduleDuration}
          extraStartTimes={startTime.slice(1)}
          baseMinute={
            Array.isArray(startTime) && startTime[0] && startTime[0].includes(':')
              ? Number(startTime[0].split(':')[1])
              : 0
          }
          setExtraStartTimes={(newTimes) =>
            setStartTime((prev) => {
              const firstTime = Array.isArray(prev) && prev.length > 0 ? prev[0] : '';
              if (!newTimes || newTimes.length === 0) {
                return [firstTime];
              }
              return [firstTime, ...newTimes];
            })
          }
        />
      )}
      {/* Time Zone Selector and Warning Row */}
      <div className="d-flex align-items-end mb-2" style={{ gap: 16, alignItems: 'flex-end' }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <Form.Group style={{ width: '100%' }}>
            <Form.Label style={{ fontWeight: 500 }}>Time Zone</Form.Label>
            {(() => {
              // Use a value-label mapping for clarity
              const tzOptions = [
                { value: '', label: 'Select a Time Zone' },
                { value: 'America/New_York', label: 'America/New_York (Eastern)' },
                { value: 'America/Chicago', label: 'America/Chicago (Central)' },
                { value: 'America/Denver', label: 'America/Denver (Mountain)' },
                { value: 'America/Los_Angeles', label: 'America/Los_Angeles (Pacific)' },
                { value: 'UTC', label: 'UTC' },
              ];
              // Find the index of the current tzid
              const currentTzid = useThisTzid;
              const selectedIdx = tzOptions.findIndex((opt) => opt.value === currentTzid);
              return (
                <DropdownSelector
                  options={tzOptions.map((opt) => opt.label)}
                  selectedOption={selectedIdx >= 0 ? selectedIdx : 0}
                  setSelected={(idx) => {
                    if (idx === 0) {
                      return;
                    }
                    setUseThisTzid(tzOptions[idx].value);
                  }}
                  closeOnSelect
                  multiple={false}
                />
              );
            })()}
          </Form.Group>
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
          }}
        >
          {R.options.tzid && tzid && R.options.tzid !== tzid ? (
            <div
              className="text-warning"
              style={{ whiteSpace: 'pre-line', display: 'inline-block' }}
            >
              Warning: This is not your time zone, it may trigger at unexpected times.
            </div>
          ) : null}
        </div>
      </div>
      <DateSelector startDate={startDate} setStartDate={setStartDate} />
      {repeatType !== 'never' && (
        <EndRepeatSelector endRepeat={endRepeat} setEndRepeat={setEndRepeat} />
      )}
      {endRepeat === 'date' && repeatType !== 'never' && (
        <EndDateSelector endDate={endDate} setEndDate={setEndDate} />
      )}
    </Form.Group>
  );
};
